import { Response, Headers, RequestOptions } from '@angular/http';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs/Rx';

import 'rxjs/Rx';

import { environment } from '../../../environments/environment';
import { User } from './user';
import { ApplicationNotificationService } from '../application-notification.service';

@Injectable()
export class AuthenticationService {
  public logoutBehaviorSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public token: string;
  public user: User;
  public marketing = false;
  public category_manager = false;
  public hq = false;
  public store = false;
  public expiration_date: Date;

  constructor(
    private http: HttpClient,
    private router: Router,
    private notificationService: ApplicationNotificationService,
  ) {
    this.loadDataFromLocalStorage();
  }

  login(username: string, password: string): Observable<boolean> {
    const api_url = environment.API + '/api-token-auth/';
    const body = JSON.stringify({ username: username, password: password});
    return this.getToken(api_url, body);
  }

  hasGroup(groupName: string): boolean {
    for (const group of this.user.groups) {
      if (group.name === groupName) {
        return true;
      }
    }
    return false;
  }

  refreshTokenQuery(): Observable<boolean> {
    const api_url = environment.API + '/api-token-refresh/';
    const body = JSON.stringify({token: this.token});
    return this.getToken(api_url, body);
  }

  loadDataFromLocalStorage() {
    const sessionData = JSON.parse(localStorage.getItem('sessionData'));
    if (sessionData) {
      this.token = sessionData.token;
      this.user = sessionData.user;
      this.marketing = this.hasGroup('marketing');
      this.category_manager = this.hasGroup('category_manager');
      this.hq = this.hasGroup('hq');
      this.store = this.hasGroup('store');
      this.expiration_date = new Date(this.getTokenExpirationDate(this.token));
      if (this.expiration_date.getTime() < Date.now()) {
        this.logout(true);
      }
    } else {
      this.logout(true);
    }
  }

  public urlBase64Decode(str: string): string {
    let output = str.replace(/-/g, '+').replace(/_/g, '/');
    switch (output.length % 4) {
      case 0: { break; }
      case 2: { output += '=='; break; }
      case 3: { output += '='; break; }
      default: {
        throw 'Illegal base64url string!';
      }
    }
    return this.b64DecodeUnicode(output);
  }

  // https://developer.mozilla.org/en/docs/Web/API/WindowBase64/Base64_encoding_and_decoding#The_Unicode_Problem
  private b64DecodeUnicode(str: any) {
    return decodeURIComponent(Array.prototype.map.call(this.b64decode(str), (c: any) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  }

  // credits for decoder goes to https://github.com/atk
  private b64decode(str: string): string {
    let chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    let output: string = '';

    str = String(str).replace(/=+$/, '');

    if (str.length % 4 == 1) {
      throw new Error("'atob' failed: The string to be decoded is not correctly encoded.");
    }

    for (
      // initialize result and counters
      let bc: number = 0, bs: any, buffer: any, idx: number = 0;
      // get next character
      buffer = str.charAt(idx++);
      // character found in table? initialize bit storage and add its ascii value;
      ~buffer && (bs = bc % 4 ? bs * 64 + buffer : buffer,
        // and if not first of each 4 characters,
        // convert the first 8 bits to one ascii character
      bc++ % 4) ? output += String.fromCharCode(255 & bs >> (-2 * bc & 6)) : 0
    ) {
      // try to find character in table (0-63, not found => -1)
      buffer = chars.indexOf(buffer);
    }
    return output;
  }

  public getTokenExpirationDate(token: string): Date {
    let decoded: any;
    decoded = this.decodeToken(token);

    if (!decoded.hasOwnProperty('exp')) {
      return null;
    }

    let date = new Date(0); // The 0 here is the key, which sets the date to the epoch
    date.setUTCSeconds(decoded.exp);

    return date;
  }

  public decodeToken(token: string): any {
    let parts = token.split('.');

    if (parts.length !== 3) {
      throw new Error('JWT must have 3 parts');
    }

    let decoded = this.urlBase64Decode(parts[1]);
    if (!decoded) {
      throw new Error('Cannot decode the token');
    }

    return JSON.parse(decoded);
  }

  public isTokenExpired(token: string, offsetSeconds?: number): boolean {
    let date = this.getTokenExpirationDate(token);
    offsetSeconds = offsetSeconds || 0;

    if (date == null) {
      return false;
    }

    // Token expired?
    return !(date.valueOf() > (new Date().valueOf() + (offsetSeconds * 1000)));
  }

  parseResponseAndStoreData(query_response: Response): boolean {
    /**
     * Used to parse a login response from backend
     * Stores data in local storage if it is valid
     * @return true if login successfully able to parse
     **/
    const token = query_response['token'];
    const user = query_response['user'];
    if (token && user) {
      // Store jwt token in localstorage
      localStorage.setItem('sessionData', JSON.stringify( query_response ));
      this.loadDataFromLocalStorage();
      return true;
    } else {
      return false;
    }
  }

  getToken(url: string, body: string): Observable<boolean> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    return this.http.post(url, body, {headers: headers})
      .map((response: Response) => {
        return this.parseResponseAndStoreData(response);
      })
      .catch((err: any) => {
        // If we have an error, login failed
        return Observable.of(false);
      });
  }

  logout(silent: boolean = false): void {
    this.logoutBehaviorSubject.next(true);
    this.token = null;
    localStorage.removeItem('sessionData');
    this.hq = false;
    this.store = false;
    this.user = null;
    this.router.navigate(['/login']);
    if (!silent) {
      this.notificationService.addGrowl('Successfully logged out.');
    }
  }

  checkTokenRefresh(): void {
    const expirationSeconds = new Date(this.getTokenExpirationDate(this.token)).getTime() / 1000;
    const currentSeconds = new Date().getTime() / 1000;
    // 600 = 10 minutes
    const ttlBoundary = 600;
    if ((expirationSeconds - currentSeconds) < ttlBoundary) {
      console.log('Token refresh is called because TTL is < 10 minutes');
      this.refreshToken();
    }
  }

  refreshToken(): void {
    if (this.token && !this.isTokenExpired(this.token)) {
      this.refreshTokenQuery().subscribe(result => {
        if (result === true) {
          console.log('Succesfully refreshed token.');
        } else {
          this.logout(true);
        }
      });
    } else {
      this.logout(true);
    }
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/auth/authentication.service.ts